import React from 'react'
import styled from '@emotion/styled'
import { layering } from '@mobi/component-library/Foundations'
import { MysteryDataTransferObject } from '@classic/Betting-v2/DataTransferObjects/MysteryDataTransferObject'
import {
  MysteryBetContainerStyled,
  MysteryHeadingStyled,
} from '@core/Areas/MysteryBet/Components/Common/Common.styles'
import {
  SetBetError,
  SetSelectedCustomBetCombination,
  SetSelectedCustomBetType,
  state$,
} from '@core/Areas/MysteryBet/driver'
import { MysteryToggleButtonStyled } from '@core/Areas/Racing/Components/Mystery/Mystery.styles'
import { CustomMysteryBetTypes } from '@core/Areas/Racing/Components/Mystery/MysteryTypes'
import { BettingDrawer } from '@core/Components/BettingDrawer/BettingDrawer'
import { NoticeBox } from '@core/Components/NoticeBox'
import { useObservableImmutable } from '@core/Utils/hooks'
import { NumberOfBets } from '../Common/NumberOfBets'
import { ReviewMysteryDetails } from '../Common/ReviewMysteryDetails'
import { CustomizeSpend } from './Components/CustomizeSpend'

export const CustomizeBet: React.FC = () => {
  const [isCustomizeSpendOpen, setIsCustomizeSpendOpen] = React.useState(false)
  const [isDetailsOpen, setIsDetailsOpen] = React.useState(false)
  const [winStake, setWinStake] = React.useState(0)
  const [placeStake, setPlaceStake] = React.useState(0)

  const mysteryData = useObservableImmutable(state$, [
    'currentData',
  ]).currentData?.toJS() as MysteryDataTransferObject
  const selectedBetType = useObservableImmutable(state$, [
    'selectedCustomBetType',
  ]).selectedCustomBetType?.toJS()
  const selectedCombination = useObservableImmutable(state$, [
    'selectedCustomBetCombination',
  ]).selectedCustomBetCombination?.toJS()
  const selectedBetCount = useObservableImmutable(state$, [
    'selectedCustomBetCount',
  ]).selectedCustomBetCount
  const isRaceClosed = useObservableImmutable(state$, ['isRaceClosed']).isRaceClosed

  const hasQuaddie = mysteryData?.QuaddieRaceNumbers?.length === 4
  const betTypes = hasQuaddie
    ? CustomMysteryBetTypes
    : CustomMysteryBetTypes.filter(x => x.type !== 'Quaddie')
  const hasAnyDisabledOptions = betTypes.some(betType =>
    mysteryData?.DisabledCustomOptions?.includes(betType.type)
  )
  const shouldBettingDrawerBeOpen =
    !isCustomizeSpendOpen && !isRaceClosed && !!selectedBetType && !!selectedBetCount

  const shouldRenderReviewDetails = isDetailsOpen && selectedBetType && selectedCombination

  return (
    <MysteryBetContainerStyled>
      <MysteryHeadingStyled>Bet Type</MysteryHeadingStyled>
      <CustomBetButtonContainerStyled data-testid='mystery-customize-bet-type'>
        {betTypes.map(betType => {
          const isDisabled =
            isRaceClosed || mysteryData?.DisabledCustomOptions?.includes(betType.type) || false
          const buttonText =
            betType.type === 'Quaddie' && (mysteryData?.QuaddieRaceNumbers?.length || 0) > 0
              ? `${betType.name} (${mysteryData?.QuaddieRaceNumbers?.join(', ')})`
              : betType.name
          return (
            <MysteryToggleButtonStyled
              key={betType.type}
              isSelected={!isRaceClosed && selectedBetType?.type === betType.type}
              disabled={isDisabled}
              onClick={() => !isDisabled && SetSelectedCustomBetType(betType)}
              data-tid-mystery-option={buttonText}
            >
              {buttonText}
            </MysteryToggleButtonStyled>
          )
        })}
      </CustomBetButtonContainerStyled>

      {hasAnyDisabledOptions && (
        <NoticeBox
          title='Not all mystery options are available for this race.'
          noticeBoxType='Info'
          hasBorder
        />
      )}
      <br />
      {selectedBetType && (
        <>
          <MysteryHeadingStyled>Number of Combinations</MysteryHeadingStyled>
          <CustomBetButtonContainerStyled data-testid='mystery-customize-bet-combinations'>
            {selectedBetType?.options?.map(option => {
              return (
                <MysteryComboButtonStyled
                  key={option.optionNumber}
                  isSelected={
                    !isRaceClosed && selectedCombination?.optionNumber === option?.optionNumber
                  }
                  onClick={() => SetSelectedCustomBetCombination(option)}
                  disabled={isRaceClosed}
                  data-tid-mystery-option={option.optionNumber}
                >
                  <div>{option.optionNumber} </div>
                  {option.numberOfCombinations}{' '}
                  {option.numberOfCombinations === 1 ? 'Combination' : 'Combinations'}
                </MysteryComboButtonStyled>
              )
            })}
          </CustomBetButtonContainerStyled>

          {!isRaceClosed && selectedBetType && selectedCombination && (
            <NoticeBox title={selectedCombination.description} noticeBoxType='Info' hasBorder />
          )}
        </>
      )}
      <br />
      <NumberOfBets
        isDisabled={isRaceClosed || !selectedBetType || !selectedCombination}
        type='Custom'
      />

      <BettingDrawer
        shouldDisplayAboveTabNav
        forcedZIndex={layering.pageLayer}
        open={shouldBettingDrawerBeOpen && !shouldRenderReviewDetails}
        handleQuickbetClick={() => setIsCustomizeSpendOpen(true)}
      />

      {selectedBetType && selectedCombination && (
        <CustomizeSpend
          open={isCustomizeSpendOpen}
          mysteryData={mysteryData}
          betType={selectedBetType.type}
          option={selectedCombination}
          numberOfBets={selectedBetCount || 1}
          isRaceClosed={isRaceClosed}
          onClosed={() => setIsCustomizeSpendOpen(false)}
          onReviewDetails={(win: number, place: number) => {
            SetBetError(null)
            setWinStake(win)
            setPlaceStake(place)
            setIsDetailsOpen(true)
          }}
        />
      )}

      {shouldRenderReviewDetails && (
        <ReviewMysteryDetails
          open={isDetailsOpen}
          mysteryData={mysteryData}
          betType={selectedBetType.type}
          optionNumber={selectedCombination.optionNumber}
          customOption={selectedCombination}
          winStake={winStake}
          placeStake={placeStake}
          numberOfBets={selectedBetCount || 1}
          isRaceClosed={isRaceClosed}
          onClosed={() => setIsDetailsOpen(false)}
        />
      )}
    </MysteryBetContainerStyled>
  )
}

// ======
// Styles
// ======

const CustomBetButtonContainerStyled = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.5rem',
  margin: '0 0 1rem 0',
})

const MysteryComboButtonStyled = styled(MysteryToggleButtonStyled)({
  ['> :first-of-type']: {
    fontWeight: 'bold',
    marginBottom: '0.33em',
  },
})
